import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FooterComponent, HeaderComponent } from 'component-library';
import { ProductData } from 'europace-components';
import { OrderStatus } from 'frontend.client.api';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BirthdayDialogComponent } from './components/shared/birthday-dialog/birthday-dialog.component';
import { OrderService } from './services/order/order.service';
import { PartnerStateService } from './shared/partner-state.service';
import { RouterService } from './shared/router.service';
import { ThemeService } from './shared/theme.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, FooterComponent, HeaderComponent, CommonModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  protected epIconSrcPath = 'assets/europaceLogo.svg';
  protected valIconSrcPath = 'assets/value_ag.png';
  private orderLockStateSubscription: Subscription | undefined;
  private partnerSubscription: Subscription | undefined;
  private orderStateSubscription: Subscription | undefined;
  private themeSubscription: Subscription | undefined;
  private dialog = inject(MatDialog);

  public productData: ProductData[] | undefined = [];
  public partnerLogoUrl = signal('');
  public partnerLoaded = signal(false);
  public themeLoaded = signal(false);
  public productCount = signal<number | undefined>(undefined);
  public impressUrl = 'https://europace.de/impressum/';
  public dataProtectionUrl = '/datenschutz';
  public termsOfUseUrl = '/agb';
  public isStaticRoute = computed( () => {
    const path= this.routerService.routerState$.getValue().path ?? '';
    return this.routerService.staticRoutes.includes(path);
    }
  )
  public showHeader = true;

  constructor(
    private orderService: OrderService,
    private themeService: ThemeService,
    private partnerState: PartnerStateService,
    private router: Router,
    private routerService: RouterService,
  ) {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe((event) => {
      const path = (event as NavigationEnd).url;
      if(this.routerService.staticRoutes.includes(path))
        this.showHeader = false;
      else 
        this.showHeader = true;
    });
  }

  

  public handleLinkClicked(event: any): void {
    const path = event.detail.toLowerCase();
    if(this.routerService.staticRoutes.includes(path)) {
      this.routerService.router.navigate([path], {});
      return;
    }
    this.routerService.routeToInCurrentSubfolder(path);
  }

  public ngOnInit(): void {
    console.log('b2c-unterlagenshop: 0.1.2-cache-fix');

    this.getHeapIdFromEnv();

    this.themeSubscription = this.themeService.themeState$.subscribe(
      (theme) => {
        if (theme) this.themeLoaded.set(theme !== undefined);
      },
    );

    this.orderLockStateSubscription =
      this.orderService.orderLockState$.subscribe((lock) => {
        if (lock) {
          this.dialog.open(BirthdayDialogComponent, {
            maxWidth: '660px',
            disableClose: true,
          });
        }
      });

    this.partnerSubscription = this.partnerState.partnerState.subscribe(
      async (partner) => {
        if (partner) {
          await this.themeService.switchTheme(
            partner?.partnerData?.themeTemplateToken,
          );
          this.partnerLogoUrl.set(partner?.partnerData?.partnerLogoUrl ?? '');
          this.partnerLoaded.set(partner !== undefined);
        } else {
          this.partnerLoaded.set(false);
        }
      },
    );

    this.orderStateSubscription = this.orderService.orderState$.subscribe(
      (orderState) => {
        const finishedOrder: OrderStatus[] = [
          OrderStatus.PAYMENT_SUCCESS,
          OrderStatus.DELIVERED,
          OrderStatus.COMMISSIONED,
        ];
        const order = orderState.order;
        this.productData = order?.items.map((item) => ({
          name: item.productData?.name ?? '',
          price: item.productData?.price ?? 0,
        }));
        if (order && finishedOrder.includes(order?.orderStatus)) {
          this.productCount.set(undefined);
        } else {
          this.productCount.set(order?.items.length ?? undefined);
        }
      },
    );
  }

  private getHeapIdFromEnv(): void {
    if(environment.STAGE === 'local') return;
    const headScript = document.querySelector('script[data-usercentrics="Heap"]');
    if(headScript && headScript.textContent){
      headScript.textContent = headScript.textContent.replace('{HEAP_ID}', environment.HEAP_ID);
    }
    const hs = document.querySelector('script[data-usercentrics="Heap"]');
  }

  public ngOnDestroy(): void {
    this.orderLockStateSubscription?.unsubscribe();
    this.partnerSubscription?.unsubscribe();
    this.orderStateSubscription?.unsubscribe();
    this.themeSubscription?.unsubscribe();
  }
}
